import React, { useState, useEffect } from "react";
import { Button, Select, Spin, message } from "antd";
import { createVulnerability, createVulnerabilityAlias, getAllVulnerabilities, updateException } from "../api";
import { ExceptionStatus } from "../ExceptionDrawer";
import Hide from "../../components/Hide";

const { Option } = Select;

const VulnerabilityComponent = ({ exception, onClose, onResolved }) => {
    const [vulnerabilityName, setVulnerabilityName] = useState(null);
    const [showSelect, setShowSelect] = useState(false);
    const [vulnerabilities, setVulnerabilities] = useState([]);
    const [selectedAliasId, setSelectedAliasId] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const extractVulnerabilityDetails = () => {
            setVulnerabilityName(exception?.unknown_infomation);
        };

        if (exception && exception.description) {
            extractVulnerabilityDetails();
        }

        const fetchVulnerabilities = async () => {
            setLoading(true);
            try {
                const response = await getAllVulnerabilities();
                const vulnerabilitiesData = response?.data?.data || [];
                setVulnerabilities(Array.isArray(vulnerabilitiesData) ? vulnerabilitiesData : []);
            } catch (error) {
                message.error('Failed to fetch vulnerabilities');
            } finally {
                setLoading(false);
            }
        };

        fetchVulnerabilities();
    }, [exception]);

    const handleMarkAsNew = async () => {
        setLoading(true);
        if (vulnerabilityName) {
            if (exception.status === 'pending') {
                try {
                    const payload = {
                        name: vulnerabilityName,
                        alias_for: 0
                    };
                    
                    await createVulnerability(payload);
                    await updateException(exception.id);
                    message.success('Vulnerability created');
                    try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                    onClose();
                } catch (error) {
                    message.error('Failed to create vulnerability');
                } finally {
                    setLoading(false);
                }
            } else {
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            }
        } else {
            setLoading(false);
            onClose();
        }
    };

    const handleMarkAsAliasFor = () => {
        setShowSelect(true);
    };

    const handleVulnerabilitySelect = (value) => {
        setSelectedAliasId(value ?? null);
    };

    const handleSaveAlias = async () => {
        if (!selectedAliasId) return;
        setLoading(true);
        if (exception.status === 'pending') {
            try {
                const payload = {
                    name: vulnerabilityName,
                    alias_for: selectedAliasId
                };
                
                await createVulnerabilityAlias(payload);
                await updateException(exception.id);
                message.success('Alias saved');
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            } catch (error) {
                message.error('Failed to save alias');
            } finally {
                setLoading(false);
            }
        } else {
            try { if (typeof onResolved === 'function') await onResolved(); } catch {}
            onClose();
        }
    };
    
    return (
        <div style={{ marginTop: 20 }}>
            <Hide condition={exception.status === ExceptionStatus.RESOLVED}>
                <Spin spinning={loading} tip="Processing...">
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button 
                            type="primary" 
                            onClick={handleMarkAsNew}
                            disabled={loading}
                            loading={loading}
                        >
                            Mark as New
                        </Button>
                        <Button 
                            type="default" 
                            onClick={handleMarkAsAliasFor}
                            disabled={loading}
                        >
                            Mark as Alias For
                        </Button>
                        {showSelect && (
                            <>
                                <div style={{ fontSize: 12, color: '#666' }}>
                                    {vulnerabilityName ? `Select an existing vulnerability to alias "${vulnerabilityName}" to.` : 'Select an existing vulnerability to alias.'}
                                </div>
                                <Select
                                    style={{ minWidth: 220 }}
                                    placeholder="Select a vulnerability"
                                    onChange={handleVulnerabilitySelect}
                                    disabled={loading}
                                    value={selectedAliasId ?? undefined}
                                >
                                    {vulnerabilities.map((vulnerability) => (
                                        <Option key={vulnerability.id} value={vulnerability.id}>
                                            {vulnerability.name}
                                        </Option>
                                    ))}
                                </Select>
                                <Button
                                    type="primary"
                                    onClick={handleSaveAlias}
                                    disabled={loading || !selectedAliasId}
                                    loading={loading}
                                >
                                    Save
                                </Button>
                            </>
                        )}
                    </div>
                </Spin>
            </Hide>
        </div>
    );
};

export default VulnerabilityComponent;
